import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ROUTES } from '@/constants/routes';
import { useAuth } from '@/store/AuthContext';

const FEATURES = ['Live kiosk status', 'Transactions and revenue', 'Role-based access'];

export function LoginPage() {
  const { login, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname: string } } | null)?.from?.pathname ??
    ROUTES.admin.dashboard;

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  if (isAuthenticated) return <Navigate to={ROUTES.admin.dashboard} replace />;

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!username || !password) {
      setError('Username and password are required');
      return;
    }
    setLoading(true);
    try {
      await login(username, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      // network errors are already toasted by the admin client interceptor
      if (err?.response) {
        const msg = err.response.data?.message ?? 'Invalid username or password';
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-vh-100 d-flex">
      {/* Brand panel */}
      <div
        className="d-none d-lg-flex flex-column justify-content-between bg-primary text-white p-5"
        style={{ width: '45%' }}
      >
        <div className="d-flex align-items-center gap-2">
          <span
            className="d-inline-flex align-items-center justify-content-center rounded-3 bg-white text-primary"
            style={{ width: 40, height: 40 }}
          >
            <i className="bi bi-printer fs-5" />
          </span>
          <span className="text-headline-sm text-white">Kiosk Print</span>
        </div>

        <div>
          <h2 className="text-headline-xl text-white mb-3">Manage every kiosk from one place</h2>
          <p className="text-body-lg text-white-50 mb-4">
            Monitor devices, track print transactions and control who can see what.
          </p>
          <ul className="list-unstyled d-flex flex-column gap-3 mb-0">
            {FEATURES.map((f) => (
              <li key={f} className="d-flex align-items-center gap-2 text-label-lg text-white">
                <i className="bi bi-check-circle-fill" />
                {f}
              </li>
            ))}
          </ul>
        </div>

        <span className="text-body-sm text-white-50">© Kiosk Print</span>
      </div>

      {/* Form panel */}
      <div className="flex-grow-1 d-flex align-items-center justify-content-center bg-surface p-3">
        <form
          className="card shadow-kiosk-md border-0 rounded-4 p-4 p-md-5 w-100"
          style={{ maxWidth: 440 }}
          onSubmit={onSubmit}
          noValidate
        >
          <span className="text-label-sm text-on-surface-variant mb-2" style={{ letterSpacing: '0.08em' }}>
            ADMIN CONSOLE
          </span>
          <h2 className="text-headline-lg text-on-surface mb-1">Sign in</h2>
          <p className="text-body-md text-on-surface-variant mb-4">
            Use your merchant or vendor account to continue.
          </p>

          <label className="text-label-md text-on-surface mb-1" htmlFor="username">
            Email or username
          </label>
          <div className="input-group mb-3">
            <span className="input-group-text bg-surface-lowest text-on-surface-variant">
              <i className="bi bi-envelope" />
            </span>
            <input
              id="username"
              className="form-control"
              style={{ minHeight: 48 }}
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </div>

          <label className="text-label-md text-on-surface mb-1" htmlFor="password">
            Password
          </label>
          <div className="input-group mb-3">
            <span className="input-group-text bg-surface-lowest text-on-surface-variant">
              <i className="bi bi-lock" />
            </span>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              className="form-control"
              style={{ minHeight: 48 }}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={() => setShowPassword((s) => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              <i className={`bi ${showPassword ? 'bi-eye-slash' : 'bi-eye'}`} />
            </button>
          </div>

          {error && (
            <div className="alert alert-danger d-flex align-items-center gap-2 py-2 mb-3" role="alert">
              <i className="bi bi-exclamation-circle" />
              <span className="text-body-sm">{error}</span>
            </div>
          )}

          <button type="submit" className="btn btn-primary w-100 tap-target" disabled={loading}>
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" />
                Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </button>

          <p className="text-body-sm text-on-surface-variant text-center mt-4 mb-0">
            Having trouble? Contact your administrator.
          </p>
        </form>
      </div>
    </div>
  );
}